import { create } from 'zustand'
import type { ExamRegistration, GuestSession, Me, TokenResponse } from '@/api/types'
import {
  cancelProactiveRefresh,
  scheduleProactiveRefresh,
  setAccessToken,
} from '@/api/client'
import { clearAll } from '@/lib/storage'
import { forgetGuestSession, rememberGuestSession } from '@/features/exam/guestSession'

/* Session state — deliberately small.

   The access token itself lives in the api client's module scope, in memory
   only. This store holds who the user is and whether we have finished finding
   out, which is what routing decisions need. */

export type AuthStatus = 'unknown' | 'authenticated' | 'anonymous' | 'replaced'

interface SessionState {
  status: AuthStatus
  user: Me | null
  /* Whether the session that just went away was a share-link guest's.

     `status: 'anonymous'` on its own cannot say. A student whose refresh
     lapsed and a guest whose exam window closed are the same state by then,
     and they need opposite screens: the student gets a sign-in that restores
     everything, while for the guest a sign-in is meaningless — no password
     produces a guest session, only the link they arrived on, which went away
     with the session. They get `/e/expired`, which explains that and tells
     them to reopen their link.

     It is a flag rather than a fourth `AuthStatus` on purpose: the session IS
     anonymous, and every other consumer should read it that way. This is only
     the reason it became anonymous, and only the route guard cares. */
  guestSessionEnded: boolean

  signIn: (token: TokenResponse, user: Me) => void
  /* A guest sign-in is a different shape, not a variant of the above: the
     registration response carries an absolute `expires_at` rather than a
     lifetime, and there is no refresh to schedule against it. */
  signInGuest: (registration: ExamRegistration, user: Me) => void
  setUser: (user: Me) => void
  setAnonymous: () => void
  /* A different device took over this account. Shown as an explicit screen,
     never as a silent redirect — a student who is bounced out mid-exam with no
     explanation assumes the platform lost their work. */
  markReplaced: () => void
  endGuestSession: () => void
  /* A guest session that ended on its own rather than by the participant
     closing it — the window shut, the link was revoked, or the token was
     already dead by the time the page reloaded. */
  markGuestSessionEnded: () => void
  signOut: () => void
}

export const useSessionStore = create<SessionState>((set) => ({
  status: 'unknown',
  user: null,
  guestSessionEnded: false,

  /* The token response carries no user — the backend keeps identity behind
     /auth/me — so `useAuth` fetches it and hands both in together.

     Any guest session on this browser is dropped here. `useSessionBootstrap`
     restores a guest BEFORE trying the refresh cookie, so a stale guest token
     left in storage outranks the account that just signed in: the student
     comes back from their next reload silently demoted to a guest, locked out
     of the portal, and bounced off every link in it. That is not a hypothetical
     ordering — the result screen's whole closing offer is "create your
     account", so signing in with a live guest token in storage is the normal
     path through this flow, not the odd one. An account sign-in supersedes a
     guest session; the guest token is already unusable by then anyway, because
     the line above has replaced the one in memory. */
  signIn(token, user) {
    forgetGuestSession()
    setAccessToken(token.access_token)
    scheduleProactiveRefresh(token.expires_in)
    set({ status: 'authenticated', user, guestSessionEnded: false })
  },

  /* No proactive refresh is scheduled: a guest token cannot be renewed, and a
     timer that fires into a failed refresh would report the session lost while
     the exam is still perfectly usable. It simply expires with the window. */
  signInGuest(registration, user) {
    setAccessToken(registration.access_token, 'guest')
    cancelProactiveRefresh()
    rememberGuestSession(registration)
    set({ status: 'authenticated', user, guestSessionEnded: false })
  },

  setUser(user) {
    set({ status: 'authenticated', user, guestSessionEnded: false })
  },

  /* The flag is deliberately NOT cleared here. Every path that ends a guest
     session marks it and then lands on this, so clearing it would erase the
     one fact the guard needs a line before it is read. It is cleared where a
     session BEGINS instead. */
  setAnonymous() {
    set({ status: 'anonymous', user: null })
  },

  markReplaced() {
    setAccessToken(null)
    cancelProactiveRefresh()
    set({ status: 'replaced', user: null })
  },

  /* Leaving a guest exam. Deliberately narrower than `signOut`: it drops the
     guest token but leaves everything else in storage alone, because a student
     may be signed in on this browser too and must not be logged out by a guest
     finishing an exam in another tab. */
  endGuestSession() {
    setAccessToken(null)
    cancelProactiveRefresh()
    forgetGuestSession()
    set({ status: 'anonymous', user: null, guestSessionEnded: true })
  },

  markGuestSessionEnded() {
    set({ guestSessionEnded: true })
  },

  signOut() {
    setAccessToken(null)
    cancelProactiveRefresh()
    forgetGuestSession()
    // Clear the exam buffer too: leaving one student's answers on a shared
    // device for the next person to resume is not acceptable.
    clearAll()
    set({ status: 'anonymous', user: null, guestSessionEnded: false })
  },
}))

export const selectIsAdmin = (state: SessionState): boolean => state.user?.user_type === 'admin'
export const selectIsGuest = (state: SessionState): boolean => state.user?.user_type === 'guest'
/* The guest block is read last but on the same footing as the other two —
   which is the whole reason `/auth/me` returns a parallel block rather than a
   student profile with the fields blanked out. */
export const selectDisplayName = (state: SessionState): string =>
  state.user?.student?.name ?? state.user?.admin?.name ?? state.user?.guest?.name ?? ''
/** The guest's link context — what the exam and result screens route against. */
export const selectGuest = (state: SessionState): GuestSession | null =>
  state.user?.guest ?? null
